import './setupPolyfills';
import React, { useState, useEffect } from 'react';
import { TonConnectUIProvider, useTonConnectUI } from '@tonconnect/ui-react';
import { toNano } from '@ton/core';
import HomeScreen from './components/game/HomeScreen';
import Lobby from './components/game/Lobby';
import PlayerDashboard from './components/game/PlayerDashboard';
import './App.css';

// TON Connect manifest URL
const manifestUrl = 'https://lastalive.pages.dev/tonconnect-manifest.json';

// Game wallet address
const GAME_WALLET_ADDRESS = 'UQAZynsAV_cCF8HtXB3K7AJJ12yAAylmzpUUNvaCc4ruRRni';

// Constants
const ENTRY_FEE = '1'; // 1 TON
const DAILY_FEE = '0.1'; // 0.1 TON

// Check if we're in Telegram environment
const isTelegramWebApp = typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp;

function GameApp() {
  const [gameState, setGameState] = useState('home');
  const [hasEntry, setHasEntry] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [tonConnectUI] = useTonConnectUI();
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [nextCheckInTime, setNextCheckInTime] = useState(null);

  useEffect(() => {
    // Initialize the app only if we're in Telegram environment
    if (isTelegramWebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
    }
    
    // Listen for wallet connection events
    const onStatusChange = (wallet) => {
      console.log('Wallet status changed:', wallet);
      if (wallet && wallet.account?.address) {
        console.log('Wallet connected via event:', wallet.account.address);
        setWalletAddress(wallet.account.address);
        setGameState('lobby');
        if (!lastCheckIn) {
          setLastCheckIn(new Date());
          setNextCheckInTime(new Date(Date.now() + 24 * 60 * 60 * 1000));
        }
      } else {
        console.log('Wallet disconnected via event');
        setWalletAddress(null);
        setGameState('home');
        setHasEntry(false);
      }
    };

    // Subscribe to connection status changes
    const unsubscribe = tonConnectUI.onStatusChange(onStatusChange);

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [tonConnectUI, lastCheckIn]);

  const showAlert = (message) => {
    if (isTelegramWebApp) {
      window.Telegram.WebApp.showAlert(message);
    } else {
      alert(message);
    }
  };

  const handleEntry = async () => {
    console.log('Starting entry payment...');
    try {
      // Send entry fee to the game wallet
      const result = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 minutes
        messages: [
          {
            address: GAME_WALLET_ADDRESS,
            amount: toNano(ENTRY_FEE).toString(),
            payload: '' // Optional payload
          }
        ]
      });

      console.log('Transaction result:', result);
      
      if (result) {
        console.log('Entry payment successful, moving to playing state');
        setHasEntry(true);
        setGameState('playing');
        // Set initial check-in time
        const now = new Date();
        setLastCheckIn(now);
        setNextCheckInTime(new Date(now.getTime() + 24 * 60 * 60 * 1000));
        showAlert('Successfully joined the game!');
      }
    } catch (error) {
      console.error('Error joining game:', error);
      showAlert('Failed to join the game. Please try again.');
    }
  };

  const handleTax = async () => {
    try {
      // Send daily tax to the game wallet
      const result = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 minutes
        messages: [
          {
            address: GAME_WALLET_ADDRESS,
            amount: toNano(DAILY_FEE).toString(),
            payload: '' // Optional payload
          }
        ]
      });

      if (result) {
        // Update check-in times
        const now = new Date();
        setLastCheckIn(now);
        setNextCheckInTime(new Date(now.getTime() + 24 * 60 * 60 * 1000));
      }
    } catch (error) {
      console.error('Error paying daily tax:', error);
      showAlert('Failed to pay daily tax. Please try again.');
    }
  };

  const renderScreen = () => {
    console.log('Current gameState:', gameState, 'hasEntry:', hasEntry, 'connected:', tonConnectUI.connected);
    
    switch (gameState) {
      case 'playing':
        return (
          <PlayerDashboard 
            wallet={tonConnectUI.account}
            onCheckIn={handleTax}
            lastCheckIn={lastCheckIn}
            nextCheckInTime={nextCheckInTime}
          />
        );
      case 'eliminated':
        return (
          <div className="eliminated-screen">
            <h1>YOU ARE ELIMINATED</h1>
            <p>Better luck next time!</p>
            <button onClick={() => setGameState('home')}>
              Return to Home
            </button>
          </div>
        );
      case 'winner':
        return (
          <div className="winner-screen">
            <h1>YOU ARE LAST ALIVE!</h1>
            <p>Congratulations! You've won the prize pool!</p>
            <button onClick={() => setGameState('home')}>
              Return to Home
            </button>
          </div>
        );
      case 'lobby':
        return (
          <Lobby
            walletAddress={walletAddress}
            hasEntry={hasEntry}
            onEntry={handleEntry}
            onTax={handleTax}
          />
        );
      default:
        return (
          <HomeScreen />
        );
    }
  };

  return (
    <div className="App">
      {renderScreen()}
    </div>
  );
}

function App() {
  return (
    <TonConnectUIProvider manifestUrl={manifestUrl}>
      <GameApp />
    </TonConnectUIProvider>
  );
}

export default App; 